import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { arrayMove } from '@dnd-kit/sortable';
import { useAllDayRoutes } from './useAllDayRoutes';
import { useDayOptimizer } from './useDayOptimizer';
import { itineraryApi } from '../api/itinerary.api';
import { toast } from '../components/ui/Toast';
import { buildDayRouteGroups, hasMissingLegs, type DayRouteGroup } from '../utils/routeGroups';
import { applyDaySchedule, applyNextDayUpdate } from '../utils/activityOrder';
import { currentTripDay } from '../utils/dateTime';
import { legModeEntry, reorderEntry, type HistoryEntry } from '../utils/itineraryHistoryEntries';
import type { ItineraryHistory } from './useItineraryHistory';
import type { TravelMode } from '../api/directions.api';
import type { PinnedVenue } from '../components/map/VenueDetailCard';
import type { ItineraryDay } from '../types';

interface Options {
  trip: { id: string };
  days: ItineraryDay[];
  setDays: React.Dispatch<React.SetStateAction<ItineraryDay[]>>;
  pinned: PinnedVenue[];
  isLoaded: boolean;
  active: boolean;
  /**
   * ADR-0015: reorders, optimisations and travel-mode changes can be undone.
   * Pass its `setDays` as `setDays`. The read-only community map has none.
   */
  history?: ItineraryHistory;
}

/**
 * State for the unified routes view: every selected day's route at once, with
 * the per-day actions (reorder, travel mode, optimise, opening hours) that used
 * to belong to the single-day view.
 *
 * Geometry always comes from the server (`POST /trips/:id/directions/route`) —
 * this hook decides *when* to ask, never how to route.
 *
 * ADR-0025 lives in that *when*: opening the map buys the whole trip's missing
 * legs in **one** call, once per opening, after the free pass. Everything else
 * buys a single day through `wantDay`, and **no effect here reads
 * `enabledDays`**, so toggling days never adds a purchase.
 */
export function useUnifiedRoutes({ trip, days, setDays, pinned, isLoaded, active, history }: Options) {
  const { t } = useTranslation();
  const routes = useAllDayRoutes({ isLoaded, trip });

  const [enabledDays, setEnabledDays] = useState<Set<number>>(new Set());
  const [collapsedDays, setCollapsedDays] = useState<Set<number>>(new Set());
  const [busyDays, setBusyDays] = useState<Set<number>>(new Set());
  const [legUnavailable, setLegUnavailable] = useState<Record<string, string[]>>({});
  // The optimiser itself is shared with the itinerary (useDayOptimizer); this
  // hook only translates between the map's day indices and its day ids.
  const optimizer = useDayOptimizer({ tripId: trip.id, days, setDays, history });

  // Days a single-day gesture has asked for. The queue exists so two quick
  // clicks resolve one after the other instead of at once, and it is the only
  // road to a paying call.
  const [wantedDays, setWantedDays] = useState<Set<number>>(new Set());
  const [resolvingDay, setResolvingDay] = useState<number | null>(null);
  // One purchase per day per session. Without it a day whose stops can never be
  // routed — no coordinates — would keep looking unresolved and keep being
  // bought, silently, at €0,005 a turn. Same guard as useDayLegs.
  const bought = useRef<Set<string>>(new Set());
  // ADR-0025: the whole-trip purchase, at most once per opening of the map.
  // A day whose legs can never be routed comes back null every time; without
  // this the map would buy the trip again on every edit.
  const tripBought = useRef(false);
  const [tripPassDone, setTripPassDone] = useState(false);

  const wantDay = useCallback((dayIdx: number) => {
    setWantedDays(prev => (prev.has(dayIdx) ? prev : new Set(prev).add(dayIdx)));
  }, []);

  // The map opens on today when today is a day of the trip, and on the whole
  // trip — every day with stops — otherwise.
  useEffect(() => {
    if (!active) return;
    const today = currentTripDay(days);
    const idx = today ? days.findIndex(d => d.id === today.id) : -1;
    if (idx >= 0) {
      setEnabledDays(new Set([idx]));
      wantDay(idx);
      return;
    }
    setEnabledDays(new Set(days.flatMap((d, i) => (d.activities.length > 0 ? [i] : []))));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, days.length]);

  // Keep the lines in sync with the itinerary while the view is open — whether
  // `days` changed from a manual edit or from a gIA proposal applied centrally.
  // `calculate` is the free pass, cache-only by construction (useAllDayRoutes).
  useEffect(() => {
    if (active) routes.calculate(days);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days, active, routes.calculate]);

  useEffect(() => {
    if (active) return;
    routes.reset();
    setWantedDays(new Set());
    tripBought.current = false;
    setTripPassDone(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  // ADR-0025: once the free pass has answered, any leg still missing is bought
  // for the whole trip in one call. With everything cached, nothing is bought.
  useEffect(() => {
    if (!active || !routes.cachedPassDone || tripBought.current) return;
    tripBought.current = true;
    if (!hasMissingLegs(routes.allDayLegs)) {
      setTripPassDone(true);
      return;
    }
    void routes.resolveTrip(days).finally(() => setTripPassDone(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, routes.cachedPassDone]);

  /**
   * One day at a time, after the trip pass (so the opening day is not bought
   * twice), and never a day the cache has already answered in full.
   *
   * The three guards are the same three `useDayLegs` documents, in the same
   * order and for the same reasons: `cachedPassDone` stops it buying legs the
   * cache was about to hand over, `bought` stops the loop on a day that can
   * never resolve, and `resolvingDay` keeps two days from overlapping. A day
   * the server did not route at all (fewer than two placeable stops) never
   * appears in `allDayLegs`, and there is nothing there to buy either.
   */
  useEffect(() => {
    if (!active || !routes.cachedPassDone || !tripPassDone || resolvingDay !== null) return;

    const next = [...wantedDays].find(idx => {
      const day = days[idx];
      if (!day || bought.current.has(day.id)) return false;
      const cached = routes.allDayLegs.find(d => d.dayIdx === idx);
      return !!cached && cached.legs.some(leg => leg === null);
    });
    if (next === undefined) return;

    const day = days[next];
    bought.current.add(day.id);
    setResolvingDay(next);
    void routes.resolveDay(next, day.id).finally(() => {
      setResolvingDay(null);
      setWantedDays(prev => {
        const rest = new Set(prev);
        rest.delete(next);
        return rest;
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, wantedDays, resolvingDay, routes.cachedPassDone, tripPassDone, routes.allDayLegs, days]);

  const groups = useMemo(
    () => buildDayRouteGroups(days, enabledDays, routes.allDayLegs, pinned),
    [days, enabledDays, routes.allDayLegs, pinned],
  );
  // An undo runs a closure from a few steps ago; it has to see today's groups.
  const groupsRef = useRef(groups);
  groupsRef.current = groups;

  // Turning a day on asks for its legs (usually already bought by the trip pass).
  const toggleDay = useCallback((dayIdx: number) => {
    setEnabledDays(prev => {
      const next = new Set(prev);
      if (next.has(dayIdx)) next.delete(dayIdx); else next.add(dayIdx);
      return next;
    });
    wantDay(dayIdx);
  }, [wantDay]);

  /** Show/hide every day at once. Pure state: the trip pass already bought the lines. */
  const toggleAllDays = useCallback((idxs: number[]) => {
    setEnabledDays(prev => (idxs.every(i => prev.has(i)) ? new Set() : new Set(idxs)));
  }, []);

  /** Show exactly these days (a destination's, ADR-0030). Pure state, like `toggleAllDays`. */
  const showOnlyDays = useCallback((idxs: number[]) => {
    setEnabledDays(new Set(idxs));
  }, []);

  const toggleCollapsed = useCallback((dayIdx: number) => {
    setCollapsedDays(prev => {
      const next = new Set(prev);
      if (next.has(dayIdx)) next.delete(dayIdx); else next.add(dayIdx);
      return next;
    });
  }, []);

  const markBusy = useCallback((dayIdx: number, busy: boolean) => {
    setBusyDays(prev => {
      const next = new Set(prev);
      if (busy) next.add(dayIdx); else next.delete(dayIdx);
      return next;
    });
  }, []);

  /** The modes the server actually used, so a recalculation keeps the rest. */
  function currentModes(group: DayRouteGroup): (TravelMode | null)[] {
    return group.stops.slice(0, -1).map((_, i) => (group.legs[i]?.mode ?? null) as TravelMode | null);
  }

  function noteUnavailable(group: DayRouteGroup, legs: DayRouteGroup['legs']) {
    setLegUnavailable(prev => {
      const next = { ...prev };
      legs.forEach((leg, i) => {
        if (!leg || leg.mode === leg.requestedMode) return;
        const key = `${group.day.id}:${i}`;
        const existing = next[key] ?? [];
        if (!existing.includes(leg.requestedMode)) next[key] = [...existing, leg.requestedMode];
      });
      return next;
    });
  }

  /**
   * Sets the travel mode of the leg that leaves `fromId`, and recalculates that
   * day if it is on screen. Keyed by ids rather than indices so an undo finds
   * the leg after the day list or its order changed under it.
   */
  const applyLegMode = useCallback(async (dayId: string, fromId: string, mode: TravelMode | null): Promise<void> => {
    setDays(prev => prev.map(d => (d.id !== dayId ? d : {
      ...d, activities: d.activities.map(a => (a.id === fromId ? { ...a, travelMode: mode } : a)),
    })));
    // Persist on the stop: the whole-trip route call takes no per-leg overrides,
    // it reads `travelMode` off the stop (server route.service legMode).
    const persisted = itineraryApi.updateActivity(trip.id, dayId, fromId, { travelMode: mode })
      .then(() => true, () => false);

    const group = groupsRef.current.find(g => g.day.id === dayId);
    const legIdx = group ? group.stops.findIndex(s => s.activity.id === fromId) : -1;
    if (!group || legIdx < 0) {
      if (!await persisted) throw new Error('TRAVEL_MODE_NOT_SAVED');
      return;
    }

    const modes = currentModes(group);
    modes[legIdx] = mode;
    markBusy(group.dayIdx, true);
    const next = await routes.recalculateDay(
      group.dayIdx, dayId, group.stops.map(s => s.activity.id), modes,
    );
    markBusy(group.dayIdx, false);
    if (!await persisted) throw new Error('TRAVEL_MODE_NOT_SAVED');

    if (!next) { toast(t('map.routeError'), 'error'); return; }
    noteUnavailable(group, next.legs);
    if (mode && next.legs[legIdx] && next.legs[legIdx]!.mode !== mode) {
      toast(t('map.travelMode.fallbackToast', { count: 1 }), 'info' as never);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routes.recalculateDay, trip.id, t, markBusy, setDays]);

  const setLegMode = useCallback(async (dayIdx: number, legIdx: number, mode: TravelMode) => {
    const group = groups.find(g => g.dayIdx === dayIdx);
    const from = group?.stops[legIdx]?.activity;
    if (!group || !from) return;
    const before = (from.travelMode ?? null) as TravelMode | null;

    const change = async (): Promise<HistoryEntry | null> => {
      try {
        await applyLegMode(group.day.id, from.id, mode);
      } catch {
        toast(t('itinerary.toast.failed'), 'error');
        return null;
      }
      if (!history) return null;
      return legModeEntry({
        apply: m => applyLegMode(group.day.id, from.id, m as TravelMode | null),
        before, after: mode,
        message: t('itinerary.undo.legMode'),
      });
    };
    if (history) await history.run(change);
    else await change();
  }, [groups, applyLegMode, history, t]);

  const reorderDay = useCallback(async (dayIdx: number, activeId: string, overId: string) => {
    const group = groups.find(g => g.dayIdx === dayIdx);
    if (!group || activeId === overId) return;

    const acts = group.stops.map(s => s.activity);
    const oldIdx = acts.findIndex(a => a.id === activeId);
    let newIdx = acts.findIndex(a => a.id === overId);
    if (oldIdx < 0 || newIdx < 0) return;

    // The origin stop can't be dragged itself, but another stop could still be
    // dropped ahead of it — clamp so it always stays first.
    const firstNonOrigin = acts.findIndex(a => !a.isOrigin);
    if (firstNonOrigin > 0) newIdx = Math.max(newIdx, firstNonOrigin);
    if (newIdx === oldIdx) return;

    const reorder = async (): Promise<HistoryEntry | null> => {
      // Position is the source of truth, and the clock follows from it: the day is
      // rescheduled from its start hour rather than handed its own old hours
      // (ADR-0010). Travel is left out of the optimistic paint here — the server's
      // response carries the version that counted it.
      const reordered = applyDaySchedule(group.day.startTime, arrayMove(acts, oldIdx, newIdx));
      const dayId = group.day.id;
      setDays(prev => prev.map(d => (d.id === dayId ? { ...d, activities: reordered } : d)));

      markBusy(dayIdx, true);
      try {
        const { nextDayUpdate, activities } = await itineraryApi.reorderActivities(
          trip.id, dayId, reordered.map(a => a.id),
        );
        setDays(prev => {
          let updated = prev.map(d => (d.id === dayId ? { ...d, activities } : d));
          if (nextDayUpdate) updated = applyNextDayUpdate(updated, nextDayUpdate);
          return updated;
        });
        if (!history) return null;
        return reorderEntry(
          { tripId: trip.id, setDays, getDays: history.getDays },
          dayId,
          acts.filter(a => !a.isOrigin).map(a => a.id),
          activities.filter(a => !a.isOrigin).map(a => a.id),
          t('itinerary.undo.reordered'),
        );
      } catch {
        toast(t('itinerary.toast.reorderFailed'), 'error');
        return null;
      } finally {
        markBusy(dayIdx, false);
      }
    };
    if (history) await history.run(reorder);
    else await reorder();
  }, [groups, setDays, trip.id, t, markBusy, history]);

  // Adapters over the shared optimiser: the panel and the map think in day
  // indices, the optimiser (and the itinerary that also uses it) in day ids.
  const optimizeDay = useCallback(async (dayIdx: number, hint?: string) => {
    const group = groups.find(g => g.dayIdx === dayIdx);
    if (!group) return;
    await optimizer.optimize(group.day.id, group.stops.map(s => s.activity), hint);
  }, [groups, optimizer]);

  const proposalDayIdx = optimizer.proposal
    ? groups.find(g => g.day.id === optimizer.proposal!.dayId)?.dayIdx ?? null
    : null;
  const optimizingDay = optimizer.optimizingDayId
    ? groups.find(g => g.day.id === optimizer.optimizingDayId)?.dayIdx ?? null
    : null;

  /**
   * Days with at least one leg drawn. The filter bar dims the rest: a day with
   * no route yet is not broken, it is unopened — and saying so is what keeps
   * "show all" from reading like a bug.
   */
  const daysWithRoute = useMemo(
    () => new Set(routes.allDayLegs.filter(d => d.legs.some(Boolean)).map(d => d.dayIdx)),
    [routes.allDayLegs],
  );

  return {
    groups,
    loading: routes.loading,
    error: routes.error,
    daysWithRoute,
    enabledDays, toggleDay, toggleAllDays, showOnlyDays,
    collapsedDays, toggleCollapsed,
    legUnavailable,
    // A day being resolved is busy for the same reason a day being recalculated
    // is: its lines are about to change and nothing else should touch it. While
    // the trip pass runs, that is every day.
    busyDays: !tripPassDone && routes.cachedPassDone
      ? new Set(days.map((_, i) => i))
      : resolvingDay === null ? busyDays : new Set(busyDays).add(resolvingDay),
    optimizingDay,
    setLegMode, reorderDay, optimizeDay,
    aiProposal: optimizer.proposal ? { ...optimizer.proposal, dayIdx: proposalDayIdx } : null,
    discardAiProposal: optimizer.discard,
    acceptAiProposal: optimizer.accept,
  };
}
